import { FC, memo, useState, useRef } from 'react'
import { useFormContext } from '../forms/form'
import { DeviceSize, ProductImage } from 'types'
import { useTranslation } from 'react-i18next'
import IconButton from '../buttons/iconButton'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import Hint from '../text/Hint'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import Tag from '../tags/tag'
import Button from './button'
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg'
import { ReactComponent as PhotoCameraIcon } from 'assets/icons/photo_camera.svg'
import useWindowDimensions from '@/hooks/useWindowDimensions'

interface MultipleFileInputProps {
    label: string
    files: File[]
    existingImages: ProductImage[]
    onFilesChange: (files: File[]) => void
    onExistingImageDelete: (imageId: string) => void
    onImageReorder: (images: ProductImage[]) => void
    onFilesReorder: (files: File[]) => void
    required?: boolean
}

const MultipleFileInput: FC<MultipleFileInputProps> = ({ 
    label, 
    files, 
    existingImages,
    onFilesChange, 
    onExistingImageDelete,
    onImageReorder,
    onFilesReorder,
    required = false 
}) => {
    const { disableInputs, isLoading } = useFormContext()
    const { t } = useTranslation()
    const { deviceSize } = useWindowDimensions()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const cameraInputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files)
            onFilesChange([...files, ...newFiles])
            // Clear file input value for better UX
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const handleCameraFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files)
            onFilesChange([...files, ...newFiles])
            // Clear camera input value for better UX
            if (cameraInputRef.current) {
                cameraInputRef.current.value = ''
            }
        }
    }

    const handleRemoveFile = (fileToRemove: File) => {
        const updatedFiles = files.filter(file => file !== fileToRemove)
        onFilesChange(updatedFiles)
    }

    const handleDragStart = () => {
        setIsDragging(true)
    }

    const handleDragEnd = (result: DropResult): void => {
        // Prevent null destination
        if (!result.destination) return

        // Prevent reordering if inputs are disabled or loading
        if (disableInputs || isLoading) return

        // Set dragging state to false
        setIsDragging(false)

        const sourceIndex = result.source.index
        const destinationIndex = result.destination.index
        
        // Create a combined array of all products with their types
        const allProducts = [
            ...existingImages.map(img => ({ type: 'existing', data: img })),
            ...files.map(file => ({ type: 'new', data: file }))
        ]
        
        // Reorder the combined array
        const reorderedProducts = [...allProducts]
        const [draggedProduct] = reorderedProducts.splice(sourceIndex, 1)
        reorderedProducts.splice(destinationIndex, 0, draggedProduct)
        
        // Separate back into existing images and new files
        const newExistingImages: ProductImage[] = []
        const newFiles: File[] = []
        
        reorderedProducts.forEach((product, index) => {
            if (product.type === 'existing') {
                newExistingImages.push({
                    ...product.data as ProductImage,
                    order: index
                })
            } else {
                newFiles.push(product.data as File)
            }
        })
        
        // Update both arrays
        onImageReorder(newExistingImages)
        onFilesReorder(newFiles)
    }

    // Get all images (existing + new) combined
    const allImages = [
        ...existingImages.map(img => ({ ...img, isExisting: true })),
        ...files.map((file, index) => ({
            productImageId: `new-${index}`,
            url: URL.createObjectURL(file),
            fileName: file.name,
            order: existingImages.length + index,
            isExisting: false,
            file
        }))
    ]

    const isPrimaryImage = (index: number) => {
        return index === 0 // First image is always primary
    }

    const imageGridClasses = `grid gap-4 w-fit grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6`
    const imageProductClasses = (isPrimary: boolean) => 
        `relative border rounded-md overflow-hidden w-32 h-32 select-none border-2 ${isPrimary ? 'border-blue-500' : 'border-gray-400'}`
    const imageClasses = `min-w-32 min-h-32 object-cover ${isLoading ? 'animate-pulse cursor-wait' : 'cursor-move'}`
    const removeButtonClasses = `absolute top-1 right-1`

    const buttonElement = (
            <Button wideOnMobile type="button" design="primary" onClick={() => fileInputRef.current?.click()}><UploadIcon /><span>{t('buttonUploadImages')}</span></Button>
    )

    const cameraButtonElement = (
            <Button wideOnMobile type="button" design="white" onClick={() => cameraInputRef.current?.click()}><PhotoCameraIcon /><span>{t('buttonTakePhoto')}</span></Button>
    )

    return (
        <div className="input-holder-wide">
            {label ? <label>{label}{required ? '*' : ''}</label> : null}

            <Hint className='mb-2'>
                {t('multipleImagesHelp')}
            </Hint>

            <DragDropContext onDragEnd={disableInputs ? () => {} : handleDragEnd} onDragStart={handleDragStart}>
                {/* All Images (Existing + New) */}
                {allImages.length > 0 ? (
                    <div className={`all-images drag_and_drop_area pb-2 mb-2 ${isDragging ? `active` : ``}`}>
                        <Droppable droppableId="all-images" direction="horizontal" isDropDisabled={disableInputs}>
                            {(provided) => (
                                <div 
                                    className={imageGridClasses}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {allImages.map((image, index) => (
                                        <Draggable 
                                            key={image.productImageId} 
                                            draggableId={image.productImageId} 
                                            index={index}
                                            isDragDisabled={disableInputs} // Now all images can be reordered
                                        >
                                            {(provided, snapshot) => (
                                                <div 
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className={`${imageProductClasses(isPrimaryImage(index))} ${snapshot.isDragging ? 'opacity-50' : ''}`}
                                                >
                                                    <img 
                                                        src={image.url} 
                                                        alt={image.fileName}
                                                        className={imageClasses}
                                                    />
                                                    {isPrimaryImage(index) && (
                                                        <Tag colorScheme="blue" className="absolute top-1 left-1">{t('primary')}</Tag>
                                                    )}
                                                    {isLoading ||
                                                        <IconButton
                                                            icon={<CloseIcon />}
                                                            size='small'
                                                            onClick={() => {
                                                                if (image.isExisting) {
                                                                    onExistingImageDelete(image.productImageId)
                                                                } else {
                                                                    // Find the actual file object from the image data
                                                                    const fileToRemove = (image as any).file
                                                                    if (fileToRemove) {
                                                                        handleRemoveFile(fileToRemove)
                                                                    }
                                                                }
                                                            }}
                                                            disabled={disableInputs}
                                                            className={removeButtonClasses}
                                                            colorScheme='red'
                                                        />
                                                    }
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                ) : <div className={`all-images drag_and_drop_area pb-2 mb-2 flex flex-col justify-center gap-2 min-h-32 md:flex-row md:items-center`}>
                    {buttonElement}
                    {deviceSize !== DeviceSize.LG && <>{cameraButtonElement}</>}
                    </div>}
            </DragDropContext>
            
            {/* File Input as Button */}
            {allImages.length ? (
                <div className="flex flex-col gap-2 md:flex-row">
                    {buttonElement}
                    {deviceSize !== DeviceSize.LG && <>{cameraButtonElement}</>}
                </div>
            ) : null}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/*"
                multiple
                onChange={handleFileChange}
                required={required && files.length === 0 && existingImages.length === 0}
                disabled={disableInputs}
                className="hidden"
            />
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleCameraFileChange}
                disabled={disableInputs}
                className="hidden"
            />
        </div>
    )
}

export default memo(MultipleFileInput)
